import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import RichTextEditor from './RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogManager = ({ onUpdate }) => {
  const [blogs, setBlogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    author: '',
    featured_image: '',
    published: false,
    featured: false,
    author_bio: '',
    author_photo: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data.blogs || []);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setFormData({ ...formData, featured_image: imageUrl });
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, featured_image: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingBlog) {
        await axios.put(`${API}/admin/blogs/${editingBlog.id}`, formData, { headers });
        toast.success('Blog updated successfully');
      } else {
        await axios.post(`${API}/admin/blogs`, formData, { headers });
        toast.success('Blog created successfully');
      }

      resetForm();
      fetchBlogs();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog deleted successfully');
      fetchBlogs();
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      category: blog.category,
      author: blog.author,
      featured_image: blog.featured_image || '',
      published: blog.published,
      featured: blog.featured || false,
      author_bio: blog.author_bio || '',
      author_photo: blog.author_photo || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      author: '',
      featured_image: '',
      published: false,
      featured: false,
      author_bio: '',
      author_photo: ''
    });
    setEditingBlog(null);
    setShowForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContentChange = (content) => {
    setFormData({ ...formData, content });
  };

  return (
    <div className="blog-manager">
      <div className="blog-manager-header">
        <h2>Blog Management</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          <Plus size={18} /> {showForm ? 'Cancel' : 'New Blog Post'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="blog-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <input
                type="text"
                className="form-input"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Tax Advisory, Finance"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Author *</label>
            <input
              type="text"
              className="form-input"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              placeholder="Author name"
              required
            />
          </div>

          {/* Featured Image Upload */}
          <div className="form-group">
            <label className="form-label">Featured Image</label>
            <div className="image-upload-container">
              {formData.featured_image ? (
                <div className="image-preview">
                  <img src={formData.featured_image} alt="Preview" />
                  <button type="button" onClick={removeImage} className="remove-image-btn">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div 
                  className="image-upload-placeholder"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <span>Uploading...</span>
                  ) : (
                    <>
                      <Upload size={24} />
                      <span>Click to upload image</span>
                      <span className="upload-hint">JPEG, PNG, GIF, WebP (max 5MB)</span>
                    </>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Excerpt * <span className="form-hint">(Brief summary shown on homepage)</span></label>
            <textarea
              className="form-textarea"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows="3"
              placeholder="Brief summary of the blog post (2-3 sentences)"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Content * <span className="form-hint">(Use toolbar to format text)</span></label>
            <RichTextEditor
              content={formData.content}
              onChange={handleContentChange}
              placeholder="Write your blog content here..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Author Bio <span className="form-hint">(Shown at the bottom of the blog post)</span></label>
            <textarea
              className="form-textarea"
              value={formData.author_bio}
              onChange={(e) => setFormData({ ...formData, author_bio: e.target.value })}
              rows="3"
              placeholder="Short author biography (e.g., 2-3 sentences)"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Author Photo URL <span className="form-hint">(Optional — paste a public image URL)</span></label>
            <input
              type="text"
              className="form-input"
              value={formData.author_photo}
              onChange={(e) => setFormData({ ...formData, author_photo: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              />
              <span>Publish immediately</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                data-testid="blog-featured-toggle"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              />
              <span>Show on Homepage <span className="form-hint">(Featured insights grid)</span></span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading || uploading}>
              {loading ? 'Saving...' : (editingBlog ? 'Update Blog' : 'Create Blog')}
            </button>
            <button type="button" onClick={resetForm} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="blog-list">
        {blogs.length === 0 ? (
          <p className="empty-state">No blog posts yet. Create your first one!</p>
        ) : (
          blogs.map((blog) => (
            <div key={blog.id} className="blog-item">
              <div className="blog-item-header">
                <div className="blog-item-info">
                  {blog.featured_image && (
                    <div className="blog-item-thumbnail">
                      <img src={blog.featured_image} alt={blog.title} />
                    </div>
                  )}
                  <div>
                    <h3>{blog.title}</h3>
                    <p className="blog-item-meta">
                      {blog.category} • {blog.author} • {blog.read_time}
                      {blog.published ? (
                        <span className="badge badge-success"><Eye size={14} /> Published</span>
                      ) : (
                        <span className="badge badge-draft"><EyeOff size={14} /> Draft</span>
                      )}
                      {blog.featured && (
                        <span className="badge badge-success" style={{ marginLeft: '6px', background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}>★ Featured</span>
                      )}
                    </p>
                    <p className="blog-item-stats">
                      <span><Eye size={12} /> {blog.views || 0} views</span>
                      <span style={{ marginLeft: '12px' }}>❤️ {blog.likes || 0} likes</span>
                    </p>
                  </div>
                </div>
                <div className="blog-item-actions">
                  <button onClick={() => handleEdit(blog)} className="icon-button">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(blog.id)} className="icon-button danger">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <p className="blog-item-excerpt">{blog.excerpt}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogManager;
